import { Dir } from "../entities/Player";
import GameState, { PlayerMoves, getXYindex } from "./gameState";
import { Entities, ExtendedBlock, EntityKind } from "./entityKinds";
import { GameEvents } from "../constants";
import { RulesPush } from "./rules/canBePushedOnto";

const extendedBodiesMap: Map<number, ExtendedBlock[]> = new Map();
const seenExtendedBodies: Set<number> = new Set();
const pushedThisTurnId: Set<number> = new Set();
let checkPlayer: null | [number, number] = null;

let lastDir = 1;
let isDelayed = false;

let checkLinearResolutionBuffer: (() => void)[] = [];

let afterChecks: ((state: Array<Array<Entities>>) => void)[] = [];
// Returns if a square will be available, with the given force direction.
function checkLinearResolution(
  state: Array<Array<Entities>>,
  x: number,
  y: number,
  dir: Dir
): boolean {
  if (x < 0 || x > 15 || y < 0 || y > 15) {
    return false;
  }
  const idx = getXYindex(x, y);
  const entities = state[idx];
  lastDir = dir;

  // Hacky check to not push over player.
  if (Array.isArray(checkPlayer)) {
    const [p_x, p_y] = checkPlayer;
    if (p_x === x && p_y === y) {
      return false;
    }
  }

  const result = [];
  for (const entity of entities) {
    if (entity.kind !== EntityKind.ExtendedBlock) {
      const pushResult = RulesPush.canBePushedOnto(entity);
      result.push(pushResult);
    } else {
      const id = entity.sharedBlockId;
      const connectedBlocks = extendedBodiesMap.get(id);
      if (
        connectedBlocks === undefined ||
        (Array.isArray(connectedBlocks) && connectedBlocks.length === 0)
      ) {
        throw new Error("unexpected loss of extended blocks");
      }
      if (!seenExtendedBodies.has(id)) {
        seenExtendedBodies.add(id);
        for (const blocks of connectedBlocks) {
          let recursiveResult = false;
          if (dir === Dir.Down) {
            recursiveResult = checkLinearResolution(
              state,
              blocks.x,
              blocks.y + 1,
              dir
            );
          } else if (dir === Dir.Up) {
            recursiveResult = checkLinearResolution(
              state,
              blocks.x,
              blocks.y - 1,
              dir
            );
          } else if (dir === Dir.Left) {
            recursiveResult = checkLinearResolution(
              state,
              blocks.x - 1,
              blocks.y,
              dir
            );
          } else if (dir === Dir.Right) {
            recursiveResult = checkLinearResolution(
              state,
              blocks.x + 1,
              blocks.y,
              dir
            );
          }
          if (recursiveResult) {
            result.push(RulesPush.OverlapResolution.Push);
          } else {
            return false;
          }
        }
      }
    }
  }

  if (result.some((r) => r === RulesPush.OverlapResolution.Stop)) {
    return false;
  }

  state[getXYindex(x, y)].forEach((entity) => {
    if (entity.kind !== EntityKind.ExtendedBlock) {
      if (!pushedThisTurnId.has(entity.id)) {
        pushedThisTurnId.add(entity.id);
        checkLinearResolutionBuffer.push(() => {
          RulesPush.pushedSideEffect(dir, isDelayed)(entity);
        });
      }
    }
  });

  if (result.every((r) => r === RulesPush.OverlapResolution.CanMove)) {
    return true;
  }

  if (result.some((r) => r === RulesPush.OverlapResolution.Push)) {
    if (dir === Dir.Down) {
      return checkLinearResolution(state, x, y + 1, dir);
    } else if (dir === Dir.Up) {
      return checkLinearResolution(state, x, y - 1, dir);
    } else if (dir === Dir.Left) {
      return checkLinearResolution(state, x - 1, y, dir);
    } else if (dir === Dir.Right) {
      return checkLinearResolution(state, x + 1, y, dir);
    }
  }

  throw new Error("Accidentally got to end of checkLinearResolution");
}

function debugBoard(board: Entities[][]) {
  console.log("--------");
  board.forEach((cell, idx) => {
    cell.forEach(({ id, kind }) => {
      console.log(`\t[${idx % 16},${Math.floor(idx / 16)}]: ${id}, ${kind}`);
    });
  });
  console.log("--------");
}

function afterLinearResolution_aux(state: GameState) {
  return (resultOfPush: boolean) => {
    const board: Array<Array<Entities>> = state.turn_gameState.board;
    pushedThisTurnId.clear();

    if (resultOfPush) {
      afterChecks = afterChecks.concat(checkLinearResolutionBuffer);
      // Here we push the extended block pieces.
      for (const seenId of seenExtendedBodies) {
        let freeze_lastDir = lastDir;
        let delayed = isDelayed;
        for (const block of extendedBodiesMap.get(seenId) || []) {
          afterChecks.push((_board: Array<Array<Entities>>) => {
            RulesPush.pushedSideEffect(freeze_lastDir, delayed)(block);
          });
        }
      }
    }

    afterChecks.reverse();
    for (const pushed of afterChecks) {
      pushed(board);
    }
    afterChecks = [];
    isDelayed = true;

    checkLinearResolutionBuffer = [];
    seenExtendedBodies.clear();

    // Fix up mapping with compound blocks in case they have moved.
    extendedBodiesMap.clear();
    state.turn_gameState.board.forEach((cell) =>
      cell.forEach((e) => {
        if (e.kind === EntityKind.ExtendedBlock) {
          const { sharedBlockId } = e;
          if (extendedBodiesMap.has(sharedBlockId)) {
            extendedBodiesMap.get(sharedBlockId)?.push(e);
          } else {
            extendedBodiesMap.set(sharedBlockId, [e]);
          }
        }
      })
    );

    return resultOfPush;
  };
}

// Function all player moves go through.
export function checkPlayerAction(
  game_state: GameState,
  move: PlayerMoves,
  scene_event_emitter: Phaser.Events.EventEmitter
): boolean {
  isDelayed = false;
  checkPlayer = null;
  // Check move & then check turns
  extendedBodiesMap.clear();
  checkLinearResolutionBuffer = [];
  afterChecks = [];
  const state = game_state.turn_gameState;
  state.board.forEach((cell) =>
    cell.forEach((e) => {
      if (e.kind === EntityKind.ExtendedBlock) {
        const { sharedBlockId } = e;
        if (extendedBodiesMap.has(sharedBlockId)) {
          extendedBodiesMap.get(sharedBlockId)?.push(e);
        } else {
          extendedBodiesMap.set(sharedBlockId, [e]);
        }
      }
    })
  );

  if (
    [
      PlayerMoves.FaceDown,
      PlayerMoves.FaceLeft,
      PlayerMoves.FaceRight,
      PlayerMoves.FaceUp,
    ].includes(move)
  ) {
    checkPlayer = [
      game_state.turn_gameState.player.x,
      game_state.turn_gameState.player.y,
    ];
  }

  const isSuccess = checkMovementRules(game_state, move);

  // HACK - to set event after the player state is updated.
  setTimeout(() => scene_event_emitter.emit(GameEvents.PlayerMadeTurn), 0);
  return isSuccess;
}

function checkMovementRules(game_state: GameState, move: PlayerMoves): boolean {
  const state = game_state.turn_gameState;
  if (!state.player) {
    return true;
  }

  const afterLinearResolution = afterLinearResolution_aux(game_state);
  const { x, y, dir } = state.player;
  let dist;
  switch (move) {
    case PlayerMoves.MoveDown:
      dist = dir === Dir.Down ? 2 : 1;
      return afterLinearResolution(
        checkLinearResolution(
          game_state.turn_gameState.board,
          x,
          y + dist,
          Dir.Down
        )
      );
    case PlayerMoves.MoveUp:
      dist = dir === Dir.Up ? 2 : 1;
      return afterLinearResolution(
        checkLinearResolution(
          game_state.turn_gameState.board,
          x,
          y - dist,
          Dir.Up
        )
      );
    case PlayerMoves.MoveLeft:
      dist = dir === Dir.Left ? 2 : 1;
      return afterLinearResolution(
        checkLinearResolution(
          game_state.turn_gameState.board,
          x - dist,
          y,
          Dir.Left
        )
      );
    case PlayerMoves.MoveRight:
      dist = dir === Dir.Right ? 2 : 1;
      return afterLinearResolution(
        checkLinearResolution(
          game_state.turn_gameState.board,
          x + dist,
          y,
          Dir.Right
        )
      );
    case PlayerMoves.FaceDown:
      if (dir === Dir.Right) {
        return (
          afterLinearResolution(
            checkLinearResolution(
              game_state.turn_gameState.board,
              x + 1,
              y + 1,
              Dir.Down
            )
          ) &&
          afterLinearResolution(
            checkLinearResolution(
              game_state.turn_gameState.board,
              x,
              y + 1,
              Dir.Left
            )
          )
        );
      } else {
        return (
          afterLinearResolution(
            checkLinearResolution(
              game_state.turn_gameState.board,
              x - 1,
              y + 1,
              Dir.Down
            )
          ) &&
          (() =>
            afterLinearResolution(
              checkLinearResolution(
                game_state.turn_gameState.board,
                x,
                y + 1,
                Dir.Right
              )
            ))()
        );
      }
    case PlayerMoves.FaceUp:
      if (dir === Dir.Right) {
        return (
          afterLinearResolution(
            checkLinearResolution(
              game_state.turn_gameState.board,
              x + 1,
              y - 1,
              Dir.Up
            )
          ) &&
          afterLinearResolution(
            checkLinearResolution(
              game_state.turn_gameState.board,
              x,
              y - 1,
              Dir.Left
            )
          )
        );
      } else {
        return (
          afterLinearResolution(
            checkLinearResolution(
              game_state.turn_gameState.board,
              x - 1,
              y - 1,
              Dir.Up
            )
          ) &&
          afterLinearResolution(
            checkLinearResolution(
              game_state.turn_gameState.board,
              x,
              y - 1,
              Dir.Right
            )
          )
        );
      }
    case PlayerMoves.FaceRight:
      if (dir === Dir.Up) {
        return (
          afterLinearResolution(
            checkLinearResolution(
              game_state.turn_gameState.board,
              x + 1,
              y - 1,
              Dir.Right
            )
          ) &&
          afterLinearResolution(
            checkLinearResolution(
              game_state.turn_gameState.board,
              x + 1,
              y,
              Dir.Down
            )
          )
        );
      } else {
        return (
          afterLinearResolution(
            checkLinearResolution(
              game_state.turn_gameState.board,
              x + 1,
              y + 1,
              Dir.Right
            )
          ) &&
          afterLinearResolution(
            checkLinearResolution(
              game_state.turn_gameState.board,
              x + 1,
              y,
              Dir.Up
            )
          )
        );
      }
    case PlayerMoves.FaceLeft:
      if (dir === Dir.Up) {
        return (
          afterLinearResolution(
            checkLinearResolution(
              game_state.turn_gameState.board,
              x - 1,
              y - 1,
              Dir.Left
            )
          ) &&
          afterLinearResolution(
            checkLinearResolution(
              game_state.turn_gameState.board,
              x - 1,
              y,
              Dir.Down
            )
          )
        );
      } else {
        return (
          afterLinearResolution(
            checkLinearResolution(
              game_state.turn_gameState.board,
              x - 1,
              y + 1,
              Dir.Left
            )
          ) &&
          afterLinearResolution(
            checkLinearResolution(
              game_state.turn_gameState.board,
              x - 1,
              y,
              Dir.Up
            )
          )
        );
      }
  }
  console.warn(`Unhandled player movement ${move}`);
  return false;
}
